import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import type { ESKResponse } from '@/services/api/api.types'

import { useGlobalState } from './useGlobalState'

export type ESKWithId = ESKResponse & { eskId: string }

/**
 * Hook that fetches and combines ESKs from both sponsor and pool sources.
 * Provides proper loading state tracking to prevent showing raw IDs before data is ready.
 */
export function useAllEsks() {
  const { api } = useGlobalState()

  const sidekicksQuery = useQuery({
    queryKey: ['sponsor', 'sidekicks'],
    queryFn: api.getSidekicks,
    initialData: {
      mskTypes: [],
      eskTypes: [],
    },
  })

  const sidekickPoolQuery = useQuery({
    queryKey: ['sponsor', 'sidekick-pool'],
    queryFn: api.getSidekickPool,
    initialData: null,
  })

  // Track whether the initial fetch has completed for both queries.
  // With initialData, isLoading is always false, so we use isFetching to know
  // when the real data has arrived.
  const isReady = !sidekicksQuery.isFetching && !sidekickPoolQuery.isFetching

  const allEsks = useMemo((): ESKWithId[] => {
    const sponsorEsks = sidekicksQuery.data?.eskTypes ?? []
    const poolEsks = sidekickPoolQuery.data?.eskTypes ?? []

    // Get types of sponsor's existing ESKs to filter pool duplicates
    const sponsorEskTypes = new Set(sponsorEsks.map(esk => esk.type))

    // Convert sponsor ESKs (use type as eskId)
    const sponsorEskOptions: ESKWithId[] = sponsorEsks.map(esk => ({
      ...esk,
      eskId: esk.type,
    }))

    // Add pool ESKs that aren't already in sponsor's list
    const poolEskOptions: ESKWithId[] = poolEsks
      .filter(esk => !sponsorEskTypes.has(esk.type))
      .map(esk => ({
        ...esk,
        eskId: esk.type,
      }))

    // Deduplicate by eskId to handle any duplicates within source arrays
    const combined = [...sponsorEskOptions, ...poolEskOptions]
    const uniqueByType = new Map<string, ESKWithId>()
    for (const esk of combined) {
      if (uniqueByType.has(esk.eskId)) {
        console.warn(`[ESK] Duplicate ESK detected: ${esk.eskId} (${esk.name})`)
      } else {
        uniqueByType.set(esk.eskId, esk)
      }
    }
    return Array.from(uniqueByType.values())
  }, [sidekicksQuery.data, sidekickPoolQuery.data])

  /**
   * Look up an ESK name by its ID.
   * Returns null if data isn't ready yet or if the ESK isn't found.
   */
  const getEskName = useCallback(
    (eskId: string | null | undefined): string | null => {
      if (!eskId) return null
      if (eskId === 'PSK') return 'Personal Sidekick'
      if (!isReady) return null
      return allEsks.find(e => e.eskId === eskId)?.name ?? null
    },
    [allEsks, isReady],
  )

  /**
   * Look up an ESK by its ID.
   * Returns undefined if not found.
   */
  const getEsk = useCallback(
    (eskId: string | null | undefined): ESKWithId | undefined => {
      if (!eskId) return undefined
      return allEsks.find(e => e.eskId === eskId)
    },
    [allEsks],
  )

  return {
    allEsks,
    isReady,
    isLoading: sidekicksQuery.isLoading || sidekickPoolQuery.isLoading,
    isFetching: sidekicksQuery.isFetching || sidekickPoolQuery.isFetching,
    getEskName,
    getEsk,
    // Expose raw queries for cases that need more control
    sidekicksQuery,
    sidekickPoolQuery,
  }
}

